import { Trans, useLingui } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";

import {
  type CreateMedicationsBulk,
  FrequencyUnit,
  apiPostMedicationsBulk,
} from "../../../api";
import { markReadsStale } from "../../../api/query-client";
import { analytics } from "../../../app/analytics";
import { AppModel } from "../../../app/app-model";
import {
  type SupportedMedications,
  useSupportedMedications,
} from "../../../catalogs/supported-medications";
import { HelperTooltip } from "../../../components/ui-kit/base/helper-tooltip";
import { Dialog, DialogBody } from "../../../components/ui-kit/dialog";
import { NumberInput } from "../../../components/ui-kit/fields/number-input";
import { Radio } from "../../../components/ui-kit/fields/radio";
import { Select } from "../../../components/ui-kit/fields/select";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import {
  CollectModeProps,
  RecordDialogActions,
  RecordDialogTitle,
  ADD_DIALOG_CLASS,
  ADD_DIALOG_WIDTH,
} from "../../../components/ui-kit/record-dialog-chrome";
import {
  RowAccordion,
  useRowAccordion,
} from "../../../components/ui-kit/tables/row-accordion";
import { assert, createRowId } from "../../../utils/utils";
import { frequencyUnits } from "../frequency-unit";
import {
  GroupOnlyCheckbox,
  groupOnlyHint,
  isGroupOnly,
  MedicationGroupSelect,
  MedicationPicker,
  needsGroupSelection,
} from "./medication-name-fields";
import { medicationCreatePayload } from "./medication-payload";
import {
  endedDateToSubmit,
  MedicationPeriodFields,
} from "./medication-period-fields";

/** The record this dialog builds, for callers using collect mode. */
export type MedicationPayload = ReturnType<typeof toMedicationPayload>;

export const AddMedicationsDialog = observer(function AddMedicationsDialog({
  app,
  onClose,
  onSaved,
  onCollect,
  patientId,
  docked,
}: {
  app: AppModel;
  onClose: () => void;
  onSaved: () => void;
} & CollectModeProps<MedicationPayload>) {
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      width={ADD_DIALOG_WIDTH}
      className={ADD_DIALOG_CLASS}
      docked={docked}
    >
      <AddMedicationsForm
        app={app}
        onClose={onClose}
        onSaved={onSaved}
        onCollect={onCollect}
        patientId={patientId}
        docked={docked}
      />
    </Dialog>
  );
});

// Rendered inside `Dialog` so the catalog hook suspends into the dialog's own
// Suspense boundary (spinner in-frame) rather than blanking the page behind it.
const AddMedicationsForm = observer(function AddMedicationsForm({
  app,
  onClose,
  onSaved,
  onCollect,
  patientId,
  docked,
}: {
  app: AppModel;
  onClose: () => void;
  onSaved: () => void;
} & CollectModeProps<MedicationPayload>) {
  const { t } = useLingui();
  const medications = useSupportedMedications();
  const recordPatientId = patientId ?? app.requirePatient().patientId;

  const accordion = useRowAccordion(createRow, [createRow()]);

  const hasValidRows =
    accordion.rows.length > 0 &&
    accordion.rows.every((r) => isMedicationRowComplete(medications, r));

  async function save() {
    const items = accordion.rows.map((r) =>
      toMedicationPayload(medications, r, recordPatientId),
    );

    // Collect mode: the extraction reviewer builds records to ingest later
    // with the rest of a document's approved entities, so this dialog hands
    // them back instead of writing them now. Same form, same validation.
    if (onCollect) {
      onCollect(
        items.map((item) => ({
          ...item,
          ...medications.combinationDose(
            item.medication_group,
            item.medication_name,
          ),
        })),
      );
      onClose();
      return;
    }
    try {
      const response = await apiPostMedicationsBulk({
        body: {
          number_of_datapoints: items.length,
          medications: items,
        },
      });

      if (response.data != null) {
        markReadsStale();
        analytics.track("Medication Added", { count: items.length });
        onSaved();
        onClose();
      }
    } catch (error) {
      window.handleError(error);
    }
  }

  return (
    <>
      <RecordDialogTitle>
        <Trans>Add medications</Trans>
      </RecordDialogTitle>

      <Form className="flex min-h-0 flex-1 flex-col" onSubmit={() => save()}>
        <DialogBody className="overflow-x-hidden">
          <RowAccordion
            {...accordion}
            summaryLabel={(row, index) =>
              rowSummaryLabel(row) ?? t`Medication ${index + 1}`
            }
            renderRow={(row) => <RowForm row={row} medications={medications} />}
            isRowComplete={
              docked
                ? (row) => isMedicationRowComplete(medications, row)
                : undefined
            }
            keepHeight={docked}
          />
        </DialogBody>

        <RecordDialogActions
          disabled={!hasValidRows}
          started={docked ? accordion.startedRowCount : undefined}
        >
          <Trans>Add</Trans>
        </RecordDialogActions>
      </Form>
    </>
  );
});

const RowForm = observer(function RowForm({
  row,
  medications,
}: {
  row: MedicationRow;
  medications: SupportedMedications;
}) {
  const { t } = useLingui();
  const fixedDose = medications.combinationDose(
    row.medication_group,
    row.medication_name,
  );

  return (
    <FormTable dataAdd className="max-w-full">
      <FormRow
        label={t`Medication`}
        data={<MedicationPicker medications={medications} model={row} />}
        mandatory={!isGroupOnly(row.medication_name)}
      />
      <FormRow
        label={t`Group only`}
        data={
          <span className="flex items-center gap-1">
            <GroupOnlyCheckbox model={row} />
            <HelperTooltip content={groupOnlyHint} />
          </span>
        }
      />
      {needsGroupSelection(medications, row) && (
        <FormRow
          label={t`Group`}
          data={<MedicationGroupSelect medications={medications} model={row} />}
          mandatory
        />
      )}
      <MedicationPeriodFields model={row} />
      <FormRow
        label={t`Dose`}
        data={
          fixedDose == null ? (
            <NumberInput
              model={row}
              attr="medication_dose"
              min={0}
              width={80}
            />
          ) : (
            <NumberInput
              value={fixedDose.medication_dose}
              disabled
              width={80}
            />
          )
        }
        mandatory={fixedDose == null}
      />
      <FormRow
        label={t`Unit`}
        data={
          fixedDose == null ? (
            <TextInput model={row} attr="medication_unit" width={80} />
          ) : (
            <TextInput value={fixedDose.medication_unit} disabled width={80} />
          )
        }
        mandatory={fixedDose == null}
      />
      <FormRow
        label={t`Dosage`}
        data={
          <div className="flex items-center gap-1">
            {[0, 1, 2, 3].map((i) => (
              <NumberInput
                key={i}
                min={0}
                value={row.medication_dosage[i]}
                onChange={(v) => {
                  row.medication_dosage[i] = v ?? 0;
                }}
                width={50}
              />
            ))}
          </div>
        }
        mandatory
      />
      <FormRow
        label={t`Frequency (repeat every)`}
        data={
          <div className="flex items-center gap-1">
            <NumberInput
              model={row}
              attr="medication_frequency_regular_repeat_every"
              min={1}
              step={1}
              width={80}
            />
            <span className="inline-block w-28">
              <Select
                model={row}
                attr="medication_frequency_regular_repeat_every_unit"
                values={frequencyUnits}
                required
                ariaLabel={t`Frequency unit`}
              />
            </span>
          </div>
        }
      />
      <FormRow
        label={t`Comment`}
        data={
          <TextInput
            model={row}
            attr="comment"
            multiline="autosize"
            width={330}
          />
        }
      />
      <FormRow
        label={t`Add change event`}
        data={
          <Radio
            values={{ no: t`No`, yes: t`Yes` }}
            value={row.medication_change_auto_event ? "yes" : "no"}
            onChange={(v) => {
              row.medication_change_auto_event = v === "yes";
            }}
          />
        }
      />
    </FormTable>
  );
});

type MedicationDosage = [number, number, number, number];

type MedicationRow = {
  __rowId: string;
  medication_group: string | null;
  medication_name: string | null;
  medication_started: string | null;
  medication_ended: string | null;
  medication_has_ended: boolean;
  medication_dose: number | null;
  medication_unit: string | null;
  medication_frequency_regular_repeat_every: number;
  medication_frequency_regular_repeat_every_unit: FrequencyUnit;
  medication_dosage: MedicationDosage;
  medication_change_auto_event: boolean;
  comment: string | null;
};

function createRow(): MedicationRow {
  return observable({
    __rowId: createRowId(),
    medication_group: null,
    medication_name: null,
    medication_started: null,
    medication_ended: null,
    medication_has_ended: false,
    medication_dose: null,
    medication_unit: null,
    medication_frequency_regular_repeat_every: 1,
    medication_frequency_regular_repeat_every_unit: FrequencyUnit.days,
    medication_dosage: [0, 0, 0, 0] as MedicationDosage,
    medication_change_auto_event: false,
    comment: null,
  });
}

// The accordion header for a collapsed row: whatever identifies the medication
// so far. A group-only row is identified by its group, since its name carries no
// information.
function rowSummaryLabel(row: MedicationRow): string | null {
  if (isGroupOnly(row.medication_name)) return row.medication_group;
  return row.medication_name;
}

// A group-only row arguably has no strength to state either, but dose and unit
// stay required for it: the create schema accepts null, while the *response*
// schema types both as non-nullable, so such a record may not read back. Only
// combination drugs send null today, and for those the backend has the
// catalog's components to fill from.
function isMedicationRowComplete(
  medications: SupportedMedications,
  row: MedicationRow,
): boolean {
  if (!row.medication_group || !row.medication_name) {
    return false;
  }
  if (
    medications.combinationDose(row.medication_group, row.medication_name) ==
      null &&
    (row.medication_dose == null || !row.medication_unit)
  ) {
    return false;
  }
  return row.medication_dosage.some((d) => d > 0);
}

function toMedicationPayload(
  medications: SupportedMedications,
  row: MedicationRow,
  patientId: number,
): CreateMedicationsBulk["medications"][number] {
  // The API requires null dose/unit for a combination drug and fills in the
  // catalog dose; values typed before the switch are dropped.
  const doseUnitDisabled =
    medications.combinationDose(row.medication_group, row.medication_name) !=
    null;

  // Only reached for a row isMedicationRowComplete has accepted, which is where
  // the identity fields stop being optional.
  assert(
    row.medication_group != null && row.medication_name != null,
    "Medication row submitted without a group or a name",
  );

  return medicationCreatePayload({
    medication_group: row.medication_group,
    medication_name: row.medication_name,
    medication_started: row.medication_started,
    medication_ended: endedDateToSubmit(row),
    medication_dose: doseUnitDisabled ? null : row.medication_dose,
    medication_unit: doseUnitDisabled ? null : row.medication_unit,
    medication_dosage: row.medication_dosage,
    medication_frequency_regular_repeat_every:
      row.medication_frequency_regular_repeat_every,
    medication_frequency_regular_repeat_every_unit:
      row.medication_frequency_regular_repeat_every_unit,
    // No input: this dialog only offers the repeat-every form.
    medication_frequency_on_days: null,
    medication_change_auto_event: row.medication_change_auto_event,
    comment: row.comment,
    patient_id: patientId,
  });
}
